import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import FormField from '../components/ui/FormField'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import { cloneEmptyPolicyForm, mapPolicyToForm, validatePolicyForm, buildPolicyPayload } from './public-api-access/publicApiLimitPolicyForm'
import { POLICY_STATUS_OPTIONS, formatSubjectDisplay } from './public-api-access/publicApiAccessPageUtils'
import { listExternalIdentities, getExternalIdentity, updateExternalIdentityStatus, updateExternalIdentityLimits } from '../services/externalIdentityAdminService'

const initialFilters = {
  status: '',
  username: '',
  providerUserId: '',
  linkedUserId: '',
  lastSeenFrom: '',
  lastSeenTo: '',
  search: '',
}

function getStatusPillClass(status) {
  const normalized = String(status || '').trim().toLowerCase()
  if (normalized === 'active') return ui.badges.success
  if (normalized === 'blocked' || normalized === 'archived' || normalized === 'disabled') return ui.badges.danger
  return ui.badges.neutral
}

export default function TelegramMiniAppUsersAdmin() {
  const [filters, setFilters] = useState(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)
  const [refreshTick, setRefreshTick] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [selectedIdentity, setSelectedIdentity] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [limitForm, setLimitForm] = useState(cloneEmptyPolicyForm())
  const [limitErrors, setLimitErrors] = useState({})
  const [pendingStatus, setPendingStatus] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    listExternalIdentities({ provider: 'telegram', ...appliedFilters })
      .then((data) => {
        if (cancelled) return
        setItems(Array.isArray(data) ? data : [])
      })
      .catch((requestError) => {
        if (cancelled) return
        setError(requestError?.message || 'Unable to load Telegram identities')
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appliedFilters, refreshTick])

  useEffect(() => {
    if (!selectedId) {
      setSelectedIdentity(null)
      setDetailError('')
      setLimitForm(cloneEmptyPolicyForm())
      return
    }

    let cancelled = false
    setDetailLoading(true)
    setDetailError('')
    getExternalIdentity(selectedId)
      .then((data) => {
        if (cancelled) return
        setSelectedIdentity(data)
        setLimitForm(mapPolicyToForm(data.effectivePolicy || {}))
      })
      .catch((requestError) => {
        if (cancelled) return
        setDetailError(requestError?.message || 'Unable to load Telegram identity details')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, refreshTick])

  const summary = useMemo(() => ({
    total: items.length,
    active: items.filter((identity) => identity.status === 'active').length,
    blocked: items.filter((identity) => identity.status === 'blocked').length,
    archived: items.filter((identity) => identity.status === 'archived').length,
  }), [items])

  const selectedRow = selectedId ? items.find((identity) => identity.id === selectedId) || null : null
  const detail = selectedIdentity || selectedRow
  const detailIdentity = detail?.identity || detail
  const effectivePolicy = detail?.effectivePolicy || selectedRow?.effectivePolicy || null

  function applyFilters() {
    setAppliedFilters({ ...filters })
  }

  function resetFilters() {
    setFilters(initialFilters)
    setAppliedFilters(initialFilters)
  }

  function openIdentity(identity) {
    setSelectedId(identity.id)
    setSelectedIdentity(identity)
    setLimitForm(mapPolicyToForm(identity.effectivePolicy || {}))
  }

  async function saveLimits(event) {
    event.preventDefault()
    if (!selectedId) return
    const errors = validatePolicyForm(limitForm)
    delete errors.subjectType
    delete errors.subjectId
    delete errors.endpointGroup
    setLimitErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      await updateExternalIdentityLimits(selectedId, buildPolicyPayload(limitForm))
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to update Telegram identity limits')
    } finally {
      setSaving(false)
    }
  }

  async function updateStatus() {
    if (!pendingStatus) return
    setSaving(true)
    try {
      await updateExternalIdentityStatus(pendingStatus.identity.id, pendingStatus.status)
      setPendingStatus(null)
      setRefreshTick((value) => value + 1)
    } catch (requestError) {
      setError(requestError?.message || 'Unable to update Telegram identity status')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState text="Loading Telegram Mini App users..." />
      </PageShell>
    )
  }

  if (error && items.length === 0) {
    return (
      <PageShell>
        <ErrorState text={error} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
      </PageShell>
    )
  }

  return (
    <PageShell className="space-y-4">
      <PageHeader
        title="Telegram Mini App Users"
        subtitle="Manage Telegram-linked ExternalIdentity records, their status, and their public API access limits."
        actions={<Button variant="secondary" onClick={() => setRefreshTick((value) => value + 1)}>Refresh</Button>}
      />

      {error && items.length > 0 ? <ErrorState text={error} /> : null}

      <SectionCard title="Filters" subtitle="Provider is fixed to telegram. Search by username, telegram user id, or linked app user id.">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input value="telegram" readOnly />
          <Input placeholder="Status" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} />
          <Input placeholder="Username" value={filters.username} onChange={(event) => setFilters((current) => ({ ...current, username: event.target.value }))} />
          <Input placeholder="Telegram user id" value={filters.providerUserId} onChange={(event) => setFilters((current) => ({ ...current, providerUserId: event.target.value }))} />
          <Input placeholder="Linked user id" value={filters.linkedUserId} onChange={(event) => setFilters((current) => ({ ...current, linkedUserId: event.target.value }))} />
          <Input placeholder="Search" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          <Input type="date" value={filters.lastSeenFrom} onChange={(event) => setFilters((current) => ({ ...current, lastSeenFrom: event.target.value }))} />
          <Input type="date" value={filters.lastSeenTo} onChange={(event) => setFilters((current) => ({ ...current, lastSeenTo: event.target.value }))} />
          <div className="flex items-end gap-2 xl:col-span-4">
            <Button onClick={applyFilters}>Apply filters</Button>
            <Button variant="secondary" onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Summary">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['Total', summary.total],
            ['Active', summary.active],
            ['Blocked', summary.blocked],
            ['Archived', summary.archived],
          ].map(([label, value]) => (
            <div key={label} className="rounded border border-outline bg-surface-elevated p-4">
              <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
              <div className="mt-1 text-2xl font-semibold">{value}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Telegram identities">
        {items.length === 0 ? (
          <EmptyState title="No Telegram users found" description="Try widening the filters or checking if Telegram auth has been used yet." />
        ) : (
          <DataTable
            columns={[
              { key: 'providerUserId', label: 'Telegram user ID', sortable: true },
              { key: 'username', label: 'Username', sortable: true },
              { key: 'firstName', label: 'First name', sortable: true },
              { key: 'lastName', label: 'Last name', sortable: true },
              { key: 'linkedUser', label: 'Linked user', sortable: true, sortAccessor: (identity) => identity.linkedUser?.email || identity.linkedUser?.name || identity.linkedUser?.id || '' },
              { key: 'status', label: 'Status', sortable: true },
              { key: 'lastSeenAt', label: 'Last seen', sortable: true, sortAccessor: (identity) => identity.lastSeenAt || '' },
              { key: 'lastAuthAt', label: 'Last auth', sortable: true, sortAccessor: (identity) => identity.lastAuthAt || '' },
              { key: 'effectiveLimit', label: 'Effective limit', sortable: true, sortAccessor: (identity) => Number(identity.effectiveLimit?.maxRequests || 0) },
              { key: 'actions', label: 'Actions' },
            ]}
            rows={items}
            renderRow={(identity) => (
              <tr key={identity.id}>
                <td className={ui.tableCellText}>{identity.providerUserId}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.username)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.firstName)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.lastName)}</td>
                <td className={ui.tableCellText}>{identity.linkedUser?.email || identity.linkedUser?.name || formatEmptyValue(identity.linkedUser?.id)}</td>
                <td className={ui.tableCell}><span className={getStatusPillClass(identity.status)}>{identity.status}</span></td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.lastSeenAt ? formatDateTime(identity.lastSeenAt) : null)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.lastAuthAt ? formatDateTime(identity.lastAuthAt) : null)}</td>
                <td className={ui.tableCellText}>{formatEmptyValue(identity.effectiveLimit?.maxRequests ?? null)}</td>
                <td className={ui.tableCell}>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => openIdentity(identity)}>View details</Button>
                    <Button size="sm" variant="secondary" onClick={() => { setSelectedId(identity.id); setSelectedIdentity(null) }}>Set limits</Button>
                    {identity.status !== 'blocked' ? <Button size="sm" variant="danger" onClick={() => setPendingStatus({ identity, status: 'blocked' })}>Block</Button> : <Button size="sm" variant="secondary" onClick={() => setPendingStatus({ identity, status: 'active' })}>Unblock</Button>}
                    {identity.status !== 'archived' ? <Button size="sm" variant="danger" onClick={() => setPendingStatus({ identity, status: 'archived' })}>Archive</Button> : null}
                  </div>
                </td>
              </tr>
            )}
          />
        )}
      </SectionCard>

      <SectionCard title="Selected Telegram user" subtitle={detail ? `${detailIdentity.providerUserId || '-'} ${formatSubjectDisplay('external_identity', detailIdentity.id)}` : 'Choose an identity to inspect its metadata, limits, and usage.'}>
        {!selectedId ? (
          <EmptyState title="No identity selected" description="Click View details to inspect a Telegram identity." />
        ) : detailLoading ? (
          <LoadingState text="Loading Telegram identity details..." />
        ) : detailError ? (
          <ErrorState text={detailError} action={<Button onClick={() => setRefreshTick((value) => value + 1)}>Retry</Button>} />
        ) : detail ? (
          <div className="grid gap-4 xl:grid-cols-[1.3fr_0.9fr]">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Provider</div><div className="mt-1 text-sm">{detailIdentity.provider}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Linked user</div><div className="mt-1 text-sm">{detail?.linkedUser?.email || detail?.linkedUser?.name || formatEmptyValue(detail?.linkedUser?.id)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Last seen</div><div className="mt-1 text-sm">{formatEmptyValue(detailIdentity.lastSeenAt ? formatDateTime(detailIdentity.lastSeenAt) : null)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Last auth</div><div className="mt-1 text-sm">{formatEmptyValue(detailIdentity.lastAuthAt ? formatDateTime(detailIdentity.lastAuthAt) : null)}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Metadata</div><div className="mt-1 text-sm">{detailIdentity.metadata ? Object.entries(detailIdentity.metadata).map(([key, value]) => `${key}: ${String(value)}`).join(', ') : 'No metadata stored'}</div></div>
                <div className="rounded border border-outline bg-surface-elevated p-3"><div className="text-xs uppercase text-muted-foreground">Recent public recognitions</div><div className="mt-1 text-sm">{detail?.recentPublicRecognitionCount}</div></div>
              </div>
              <SectionCard title="Recent usage" className="p-0">
                {Array.isArray(detail.usageBuckets) && detail.usageBuckets.length > 0 ? (
                  <DataTable
                    columns={[
                      { key: 'bucketType', label: 'Bucket', sortable: true },
                      { key: 'bucketStart', label: 'Start', sortable: true, sortAccessor: (bucket) => bucket.bucketStart || '' },
                      { key: 'requestCount', label: 'Requests', sortable: true, sortAccessor: (bucket) => Number(bucket.requestCount || 0) },
                      { key: 'acceptedCount', label: 'Accepted', sortable: true, sortAccessor: (bucket) => Number(bucket.acceptedCount || 0) },
                      { key: 'rejectedCount', label: 'Rejected', sortable: true, sortAccessor: (bucket) => Number(bucket.rejectedCount || 0) },
                      { key: 'costUnits', label: 'Cost', sortable: true, sortAccessor: (bucket) => Number(bucket.costUnits || 0) },
                    ]}
                    rows={detail.usageBuckets}
                    renderRow={(bucket) => (
                      <tr key={bucket.id}>
                        <td className={ui.tableCellText}>{bucket.bucketType}</td>
                        <td className={ui.tableCellText}>{formatEmptyValue(bucket.bucketStart ? formatDateTime(bucket.bucketStart) : null)}</td>
                        <td className={ui.tableCellText}>{bucket.requestCount}</td>
                        <td className={ui.tableCellText}>{bucket.acceptedCount}</td>
                        <td className={ui.tableCellText}>{bucket.rejectedCount}</td>
                        <td className={ui.tableCellText}>{bucket.costUnits}</td>
                      </tr>
                    )}
                  />
                ) : (
                  <EmptyState title="No usage buckets" description="This Telegram identity has no usage history in the selected window." />
                )}
              </SectionCard>
            </div>

            <SectionCard title="Effective limit">
              <form className="grid gap-3" onSubmit={saveLimits}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Window seconds" error={limitErrors.windowSeconds}><Input type="number" min="60" value={limitForm.windowSeconds} onChange={(event) => setLimitForm((current) => ({ ...current, windowSeconds: event.target.value }))} /></FormField>
                  <FormField label="Max requests" error={limitErrors.maxRequests}><Input type="number" min="1" value={limitForm.maxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, maxRequests: event.target.value }))} /></FormField>
                  <FormField label="Cost units" error={limitErrors.maxCostUnits}><Input type="number" min="0" value={limitForm.maxCostUnits} onChange={(event) => setLimitForm((current) => ({ ...current, maxCostUnits: event.target.value }))} /></FormField>
                  <FormField label="Concurrent jobs" error={limitErrors.maxConcurrentJobs}><Input type="number" min="0" value={limitForm.maxConcurrentJobs} onChange={(event) => setLimitForm((current) => ({ ...current, maxConcurrentJobs: event.target.value }))} /></FormField>
                  <FormField label="Daily max" error={limitErrors.dailyMaxRequests}><Input type="number" min="0" value={limitForm.dailyMaxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, dailyMaxRequests: event.target.value }))} /></FormField>
                  <FormField label="Monthly max" error={limitErrors.monthlyMaxRequests}><Input type="number" min="0" value={limitForm.monthlyMaxRequests} onChange={(event) => setLimitForm((current) => ({ ...current, monthlyMaxRequests: event.target.value }))} /></FormField>
                  <FormField label="Priority" error={limitErrors.priority}><Input type="number" min="0" value={limitForm.priority} onChange={(event) => setLimitForm((current) => ({ ...current, priority: event.target.value }))} /></FormField>
                  <FormField label="Status" error={limitErrors.status}>
                    <select className={ui.select} value={limitForm.status} onChange={(event) => setLimitForm((current) => ({ ...current, status: event.target.value }))}>
                      {POLICY_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </FormField>
                </div>
                <FormField label="Reason"><Input as="textarea" rows={3} value={limitForm.reason} onChange={(event) => setLimitForm((current) => ({ ...current, reason: event.target.value }))} /></FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Effective from" error={limitErrors.effectiveFrom}><Input type="date" value={limitForm.effectiveFrom} onChange={(event) => setLimitForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></FormField>
                  <FormField label="Effective to" error={limitErrors.effectiveTo}><Input type="date" value={limitForm.effectiveTo} onChange={(event) => setLimitForm((current) => ({ ...current, effectiveTo: event.target.value }))} /></FormField>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={saving}>Save limits</Button>
                  <Button type="button" variant="secondary" onClick={() => setLimitForm(mapPolicyToForm(effectivePolicy || {}))}>Reset</Button>
                </div>
              </form>
            </SectionCard>
          </div>
        ) : null}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingStatus)}
        title="Change identity status"
        message={pendingStatus ? `Set Telegram user ${pendingStatus.identity.providerUserId} to ${pendingStatus.status}?` : ''}
        confirmText="Apply"
        confirmVariant={pendingStatus?.status === 'blocked' ? 'danger' : 'primary'}
        loading={saving}
        onConfirm={updateStatus}
        onCancel={() => setPendingStatus(null)}
      />
    </PageShell>
  )
}
